import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import ProjectContact from 'src/app/model/company/projectContact';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import { ModalService } from 'src/app/services/modal.service';
import {
  ProjectDisplayObservableService,
  SubProjectStatusChangedEvent,
} from 'src/app/services/project-display-observable.service';
import { RequestService } from 'src/app/services/request.service';
import { TabsService } from 'src/app/services/tabs.service';
import { ContactPickerEvent } from 'src/app/shared/contact-picker/contact-picker-event';
import { StockForSignControllerRequestService } from '../client-corner/create-view-base-project/client-base-project-summary/client-product-summary/client-ip-board-summary/stock-for-sign-controller-request.service';
import { EditProjectHelper } from '../edit/edit-project-helper';
import IPBoardPart from '../../../model/product/ip-board/ip-board-part';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { AtelierRelevantAndConsideredCompleteControllerRequestService } from 'src/app/services/api/atelier/atelier-relevant-and-considered-complete-controller-request.service';

@Component({
  selector: 'edit-ip-board-project',
  templateUrl: './edit-ip-board-project.component.html',
  styleUrls: ['./edit-ip-board-project.component.scss'],
})
export class EditIPBoardProjectComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  project: Project;
  @Input()
  model: BaseProject;
  @Input()
  projectContactIdAssigned: Subject<ProjectContact[]>;
  @Output()
  public save = new EventEmitter<any>();

  disableDelete: boolean;

  constructor(
    private employeeChoicesService: EmployeeChoicesService,
    private stockForSignControllerRequestService: StockForSignControllerRequestService,
    private atelierRelevantAndConsideredCompleteControllerRequestService: AtelierRelevantAndConsideredCompleteControllerRequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private requestService: RequestService,
    private modalService: ModalService,
    private tabsService: TabsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.disableDelete =
      this.model.id == null ||
      ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(this.model.status) ||
      this.model.archived;
    this.employeeChoicesService.updateEmployeeChoices();
    if (this.project.id != null) {
      this.loadTotalAndProductionStockForSigns();
    }

    this.addSubscription(
      this.projectDisplayObservableService.getSubProjectStatusChangedSubscribable(),
      (event: SubProjectStatusChangedEvent) => {
        if (event != null && event.subProjectId === this.model.id) {
          this.disableDelete =
            this.model.id == null ||
            ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(
              this.model.status
            ) ||
            this.model.archived;
        }
      }
    );
  }

  private loadTotalAndProductionStockForSigns(): void {
    if (!CollectionHelper.isPopulated(this.project.products)) {
      return;
    }
    const promises: Promise<void>[] = this.project.products.flatMap((p) => {
      if (!ProductHelper.isIPBoard(p)) {
        return null;
      }
      const stock = this.loadStockSigns(p.boardPart);
      const otherStock = this.loadStockSigns(p.otherSideBoardPart);
      return [stock, otherStock].filter((promise) => promise != null);
    });
    CollectionHelper.filterInPlace(promises, (p) => p != null);
    if (promises.length > 0) {
      Promise.all(promises).then(() =>
        this.projectDisplayObservableService
          .getStockInfoUpdatedSubscribable()
          .next()
      );
    }
  }

  loadStockSigns(boardPart: IPBoardPart): Promise<void> {
    if (boardPart == null || boardPart.sign == null) {
      return null;
    }
    return this.stockForSignControllerRequestService
      .totalInProdInOfferStockForSign(boardPart.sign.id)
      .then((res) => {
        boardPart.sign['totalStock'] = res.totalStock;
        boardPart.sign['inProductionStock'] = res.inProductionStock;
        boardPart.sign['inOfferStock'] = res.inOfferStock;
      });
  }

  saveContacts(event: ContactPickerEvent): void {
    EditProjectHelper.handleSaveContacts(
      event,
      this.project,
      this.model,
      this.projectDisplayObservableService
    );
    this.projectDisplayObservableService
      .getProjectContactsChangedSubscribable()
      .next();
    this.save.emit(this.model);
  }

  deleteSubProject() {
    this.askDeleteSubProjectConfirmation();
  }

  private async askDeleteSubProjectConfirmation(): Promise<void> {
    const atelierRelevantAndConsideredComplete =
      await this.atelierRelevantAndConsideredCompleteControllerRequestService.getAtelierRelevantAndConsideredCompleteForBaseProjectOfProject(
        this.project.id
      );
    this.modalService.confirmDialog(
      'Job verwijderen',
      atelierRelevantAndConsideredComplete
        ? 'OPGELET! Magazijn stock werd reeds afgetrokken voor deze job. Bent u zeker dat u deze job wilt verwijderen? Indien ja, gelieve stock aantallen te corrigeren'
        : 'Bent u zeker dat u deze job wilt verwijderen?',
      (answer: boolean) => {
        if (answer) {
          this.requestService
            .delete('/subprojects/archive/' + this.model.id)
            .toPromise()
            .then(() => {
              this.model.archived = true;
              this.tabsService.closeTab(this.tabsService.getActiveTab());
            });
        }
      }
    );
  }
}
